import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Player } from '@lottiefiles/react-lottie-player';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as amplitude from '@amplitude/analytics-browser';

import {
  faCheckCircle,
  faArrowRight,
  faUtensils,
  faGem,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import config from '../../utils/config.js';
import loader from '../../loader/main_loading_animation.json'

const SubscriptionSuccessPage = (props) => {
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = props;
  
  // Parse the session ID from the URL if present
  const query = new URLSearchParams(location.search);
  const sessionId = query.get('session_id');
  
  useEffect(() => {
    // Fetch subscription details
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await fetch(`${config.API_SERVER_URL}/api/v3/details`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('userToken')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscriptionDetails(data);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // Allow a moment for the webhook to process before fetching details
    const timer = setTimeout(() => {
      fetchSubscriptionDetails();
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [sessionId]);

  // Add redirection to home page after success message display
  useEffect(() => {
    // Amplitude tracking for COMPLETED
    amplitude.track('Premium Subscription Completed', {
      plan_type: subscriptionDetails?.interval || 'unknown',
      user_id: localStorage.getItem('userId') || 'guest',
      timestamp: Date.now()
  });
    if (!loading && subscriptionDetails) {
      const redirectTimer = setTimeout(() => {
        navigate('/');
      }, 5000); // Redirect after 5 seconds
      
      return () => clearTimeout(redirectTimer);
    }
  }, [loading, subscriptionDetails, navigate]);

  return (
    <div className="flex flex-col items-center w-full bg-gradient-to-b from-white to-gray-50">
      <Helmet>
        <title>Subscription Activated | Ladle Premium</title>
      </Helmet>
      
      <div className="w-full relative bg-[#0e4621] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="h-24 w-24 mx-auto mb-6">
            <Player
              src={loader || {}}
              className="player"
              loop={false}
              autoplay
              keepLastFrame
            />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Welcome to Premium!
          </h1>
          <p className="text-xl text-white/90 max-w-xl mx-auto">
            {loading ? 'Processing your subscription...' : 'Your premium subscription has been activated successfully.'}
          </p>
          {!loading && (
            <p className="text-white/80 mt-3">
              Redirecting to home page in a few seconds...
            </p>
          )}
        </div>
      </div>
      
      <div className="w-full max-w-4xl mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="text-center">
              <div className="inline-block p-4 bg-[#0e4621]/10 rounded-full mb-4">
                <FontAwesomeIcon icon={faGem} className="h-8 w-8 text-[#0e4621]" pulse />
              </div>
              <p className="text-lg text-gray-600">Processing your subscription details...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Subscription Details Section */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-10 mx-auto max-w-3xl">
              <h2 className="text-2xl font-bold text-[#0e4621] mb-6 flex items-center">
                <FontAwesomeIcon icon={faGem} className="mr-3 text-[#0e4621]" />
                Your Subscription Details
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-5 rounded-lg">
                  <div className="flex items-center mb-3">
                    <FontAwesomeIcon icon={faGem} className="h-5 w-5 text-[#0e4621] mr-3" />
                    <h3 className="font-semibold text-gray-800">Plan Type</h3>
                  </div>
                  <p className="text-xl font-medium text-[#0e4621] capitalize">
                    {subscriptionDetails?.plan || 'Premium'} 
                    <span className="text-gray-500 text-base ml-1 capitalize">({subscriptionDetails?.interval || 'subscription'})</span>
                  </p>
                </div>
                
                <div className="bg-gray-50 p-5 rounded-lg">
                  <div className="flex items-center mb-3">
                    <FontAwesomeIcon icon={faCheckCircle} className="h-5 w-5 text-[#0e4621] mr-3" />
                    <h3 className="font-semibold text-gray-800">Status</h3>
                  </div>
                  <p className="text-xl font-medium text-green-600 capitalize">
                    {subscriptionDetails?.status || 'Active'}
                  </p>
                </div>
              </div>
              
              <div className="mt-8 flex justify-center">
                <button 
                  onClick={() => navigate('/')}
                  className="flex items-center bg-[#0e4621] text-white px-6 py-3 rounded-lg hover:bg-[#0a3419] transition-colors"
                >
                  Go to Home Page
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </button>
              </div>
            </div>
            
            {/* Premium Benefits Section */}
            <div className="bg-white rounded-xl shadow-md p-8 mx-auto max-w-3xl">
              <h2 className="text-2xl font-bold text-[#0e4621] mb-6 text-center">
                Your Premium Benefits
              </h2>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <div className="inline-block p-3 bg-[#0e4621]/10 rounded-full mb-3">
                    <FontAwesomeIcon icon={faUtensils} className="h-6 w-6 text-[#0e4621]" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1">Exclusive Recipes</h3>
                  <p className="text-sm text-gray-600">Access our full recipe collection</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <div className="inline-block p-3 bg-[#0e4621]/10 rounded-full mb-3">
                    <FontAwesomeIcon icon={faCalendarAlt} className="h-6 w-6 text-[#0e4621]" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1">Meal Planning</h3>
                  <p className="text-sm text-gray-600">Plan your meals with ease</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <div className="inline-block p-3 bg-[#0e4621]/10 rounded-full mb-3">
                    <FontAwesomeIcon icon={faGem} className="h-6 w-6 text-[#0e4621]" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1">Ad-Free</h3>
                  <p className="text-sm text-gray-600">Enjoy without interruptions</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
